import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AiInterview,
  AiInterviewPayload,
  AiInterviewSession,
  AiInterviewTranscriptLine,
  InterviewStage,
  TranscriptSpeaker,
} from '../types';
import { aiInterviewsService, getApiErrorMessage } from '../services/api';
import { INTERVIEW_MOCK_MODE } from '../utils/interviewEnv';
import { stripEchoedQuestion } from '../utils/stripEcho';
import { LiveConnectionState, LiveSessionEvents, useGeminiLiveSession } from './useGeminiLiveSession';
import { MockLiveSessionDriver, useMockLiveSession } from './useMockLiveSession';
import { useLocalSpeechRecognition } from './useLocalSpeechRecognition';

export type { InterviewStage };

export interface UseAiInterviewResult {
  loading: boolean;
  loadError: string | null;
  interviews: AiInterview[];
  selected: AiInterview | null;
  completed: AiInterview | null;
  session: AiInterviewSession | null;
  stage: InterviewStage;
  driverState: LiveConnectionState;
  aiSpeaking: boolean;
  micOn: boolean;
  micError: string | null;
  transcript: AiInterviewTranscriptLine[];
  answeredCount: number;
  maxQuestions: number;
  isMock: boolean;
  createInterview: (payload: AiInterviewPayload) => Promise<void>;
  begin: () => Promise<void>;
  submitAnswer: (text: string) => void;
  finishInterview: () => Promise<void>;
  deleteInterview: (id: number) => Promise<void>;
  reload: () => Promise<void>;
}

const EMPTY_TRANSCRIPT: AiInterviewTranscriptLine[] = [];

export function useAiInterview(applicationId: number): UseAiInterviewResult {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [interviews, setInterviews] = useState<AiInterview[]>([]);
  const [session, setSession] = useState<AiInterviewSession | null>(null);
  const [stage, setStage] = useState<InterviewStage>('IDLE');
  const [topError, setTopError] = useState<string | null>(null);
  const [transcript, setTranscript] = useState<AiInterviewTranscriptLine[]>(EMPTY_TRANSCRIPT);
  const [answeredCount, setAnsweredCount] = useState(0);

  const transcriptRef = useRef<AiInterviewTranscriptLine[]>(EMPTY_TRANSCRIPT);
  const answeredRef = useRef(0);
  const maxQuestionsRef = useRef(10);
  const interviewIdRef = useRef<number | null>(null);
  const startedRef = useRef(false);
  const finishingRef = useRef(false);
  const pendingAppendRef = useRef<AiInterviewTranscriptLine[]>([]);
  // Mic-energy + interview-end wrap-up state.
  const lastUserVoiceAtRef = useRef(0);
  const wrapArmedRef = useRef(false);
  const wrapSentRef = useRef(false);
  const wrapHandledRef = useRef(false);
  const wrapTimerRef = useRef<number | null>(null);
  const failsafeTimerRef = useRef<number | null>(null);
  const aiSpeakingRef = useRef(false);
  const driverStateRef = useRef<LiveConnectionState>('idle');

  const selected =
    interviews.find((i) => i.status === 'IN_PROGRESS') ??
    interviews.find((i) => i.status === 'READY') ??
    null;
  const completed = interviews.find((i) => i.status === 'COMPLETED') ?? null;

  const flushPending = useCallback(async () => {
    const id = interviewIdRef.current;
    if (!id || pendingAppendRef.current.length === 0) return;
    const lines = pendingAppendRef.current;
    pendingAppendRef.current = [];
    try {
      const res = await aiInterviewsService.appendTranscript(id, lines);
      const all = [...transcriptRef.current];
      setTranscript(all);
      return res.data;
    } catch {
      // Put them back so they are not lost; they will be retried on finish.
      pendingAppendRef.current = [...lines, ...pendingAppendRef.current];
      return null;
    }
  }, []);

  const finishInterview = useCallback(async () => {
    const id = interviewIdRef.current;
    if (!id || finishingRef.current) return;
    finishingRef.current = true;
    setStage('COMPLETING');
    if (wrapTimerRef.current != null) window.clearInterval(wrapTimerRef.current);
    if (failsafeTimerRef.current != null) window.clearTimeout(failsafeTimerRef.current);
    wrapTimerRef.current = null;
    failsafeTimerRef.current = null;

    try {
      await driverRef.current?.stop();
      await flushPending();

      const res = await aiInterviewsService.complete(id, transcriptRef.current);
      setInterviews((prev) => [res.data, ...prev.filter((i) => i.id !== res.data.id)]);
      setSession(null);
      setStage('COMPLETED');
    } catch (err) {
      finishingRef.current = false;
      setTopError(getApiErrorMessage(err, 'Could not complete the interview.'));
      setStage('ERROR');
    }
  }, [flushPending]);

  const armWrapWhenDone = useCallback(() => {
    if (finishingRef.current || wrapSentRef.current) return;
    if (answeredRef.current < maxQuestionsRef.current) return;
    wrapArmedRef.current = true;
    if (wrapTimerRef.current != null) return;
    wrapTimerRef.current = window.setInterval(() => {
      if (wrapSentRef.current) {
        if (wrapTimerRef.current != null) window.clearInterval(wrapTimerRef.current);
        wrapTimerRef.current = null;
        return;
      }
      // Do not wrap while the candidate is still speaking or the interviewer
      // is mid-response — a genuine pause of ~1.6s is required first.
      const userQuiet = Date.now() - lastUserVoiceAtRef.current > 1600;
      const interviewerIdle = !aiSpeakingRef.current;
      if (userQuiet && interviewerIdle && driverStateRef.current === 'open') {
        wrapSentRef.current = true;
        driverRef.current.signalWrapUp();
        if (wrapTimerRef.current != null) window.clearInterval(wrapTimerRef.current);
        wrapTimerRef.current = null;
        failsafeTimerRef.current = window.setTimeout(() => {
          if (!finishingRef.current) void finishInterview();
        }, 12000);
      }
    }, 300);
  }, [finishInterview]);

  const eventsRef = useRef<LiveSessionEvents>({
    onTranscription: (speaker: TranscriptSpeaker, text: string, isFinal: boolean) => {
      let clean = text.trim();
      if (!clean || finishingRef.current) return;

      if (!isFinal) {
        setStage(speaker === 'AI' ? 'ASKING' : 'LISTENING');
        return;
      }

      if (speaker === 'CANDIDATE' && !INTERVIEW_MOCK_MODE) {
        const aiLine = transcriptRef.current
          .filter((l) => l.speaker === 'AI')
          .pop();
        if (aiLine) {
          const stripped = stripEchoedQuestion(clean, aiLine.text);
          if (stripped === null) {
            return;
          }
          clean = stripped;
        }
      }
      if (!clean) return;

      const current = transcriptRef.current;
      const last = current[current.length - 1];
      if (last && last.speaker === speaker && last.text === clean) return;

      const line: AiInterviewTranscriptLine = {
        speaker,
        text: clean,
        timestamp: new Date().toISOString(),
      };
      const next = [...current, line];
      transcriptRef.current = next;
      setTranscript(next);

      pendingAppendRef.current.push(line);
      void flushPending();

      if (speaker === 'CANDIDATE') {
        const nextCount = answeredRef.current + 1;
        answeredRef.current = nextCount;
        setAnsweredCount(nextCount);
        armWrapWhenDone();
        setStage('PROCESSING');
      } else {
        if (aiSpeakingRef.current) {
          setStage('ASKING');
        } else {
          setStage(INTERVIEW_MOCK_MODE ? 'PROCESSING' : 'LISTENING');
        }
      }
    },
    onInterrupted: () => setStage('LISTENING'),
    onTurnComplete: () => {
      if (wrapSentRef.current) {
        if (!wrapHandledRef.current) {
          wrapHandledRef.current = true;
          void finishInterview();
        }
        return;
      }
      if (stageRef.current !== 'COMPLETING') {
        setStage(INTERVIEW_MOCK_MODE ? 'PROCESSING' : 'LISTENING');
      }
    },
    onOpen: () => setStage('INTRO'),
    onError: (message) => {
      setTopError(message);
      setStage('ERROR');
    },
    onUserVoice: (active: boolean) => {
      if (active) {
        lastUserVoiceAtRef.current = Date.now();
      }
    },
  });

  const stageRef = useRef<InterviewStage>('IDLE');
  stageRef.current = stage;

  const refreshSessionToken = useCallback(async (): Promise<AiInterviewSession | null> => {
    const id = interviewIdRef.current;
    if (!id) return null;
    try {
      const res = await aiInterviewsService.session(id);
      return res.data;
    } catch {
      return null;
    }
  }, []);

  const resumeContext = useCallback((): string | null => {
    const lines = transcriptRef.current;
    if (lines.length === 0) return null;
    const recent = lines
      .slice(-14)
      .map((l) => `${l.speaker === 'AI' ? 'Interviewer' : 'Candidate'}: ${l.text}`)
      .join('\n');
    const answered = answeredRef.current;
    const total = maxQuestionsRef.current;
    const next =
      answered >= total
        ? 'Wrap up the interview and give a brief closing, then thank the candidate.'
        : 'Ask the next interview question based on where we left off.';
    return (
      `We are already mid-interview (${answered} of ${total} questions answered so far). ` +
      `This connection was interrupted and just restarted. Here is the conversation so far, newest last:\n${recent}\n\n` +
      `Do not re-introduce yourself and do not restart the interview. ${next}`
    );
  }, []);

  const realDriver = useGeminiLiveSession(session, eventsRef.current, {
    refreshToken: refreshSessionToken,
    getResumeContext: resumeContext,
  });
  const mockDriver = useMockLiveSession(session, eventsRef.current);
  const driver = INTERVIEW_MOCK_MODE ? mockDriver : realDriver;
  const driverRef = useRef<typeof driver>(driver);
  driverRef.current = driver;

  const [driverState, setDriverState] = useState<LiveConnectionState>('idle');
  useEffect(() => setDriverState(driver.state), [driver.state]);
  useEffect(() => {
    driverStateRef.current = driver.state;
  }, [driver.state]);
  const [aiSpeaking, setAiSpeaking] = useState(false);
  useEffect(() => setAiSpeaking(driver.isAiSpeaking), [driver.isAiSpeaking]);
  useEffect(() => {
    aiSpeakingRef.current = driver.isAiSpeaking;
  }, [driver.isAiSpeaking]);

  // Candidate words come from local speech recognition (Chrome's built-in STT);
  // Gemini's live input transcription is unreliable. Active only while the
  // live driver is open and never while the interviewer is speaking.
  const sttEnabled =
    !INTERVIEW_MOCK_MODE && session != null && driverState === 'open' && stage !== 'COMPLETING' && stage !== 'COMPLETED' && stage !== 'ERROR';
  useLocalSpeechRecognition({
    enabled: sttEnabled,
    paused: driver.isAiSpeaking,
    onResult: (text: string, isFinal: boolean) => {
      if (!finishingRef.current) {
        eventsRef.current.onTranscription('CANDIDATE', text, isFinal);
      }
    },
  });

  const prepareForInterview = useCallback(async (interview: AiInterview) => {
    transcriptRef.current = [...(interview.transcript ?? [])];
    setTranscript(transcriptRef.current);
    const answered = transcriptRef.current.filter((l) => l.speaker === 'CANDIDATE').length;
    answeredRef.current = answered;
    setAnsweredCount(answered);
    maxQuestionsRef.current = interview.maxQuestions || 10;
    interviewIdRef.current = interview.id;
    startedRef.current = false;
    finishingRef.current = false;
    pendingAppendRef.current = [];
    lastUserVoiceAtRef.current = 0;
    wrapArmedRef.current = false;
    wrapSentRef.current = false;
    wrapHandledRef.current = false;
    if (wrapTimerRef.current != null) window.clearInterval(wrapTimerRef.current);
    if (failsafeTimerRef.current != null) window.clearTimeout(failsafeTimerRef.current);
    wrapTimerRef.current = null;
    failsafeTimerRef.current = null;
    setTopError(null);

    await aiInterviewsService.start(interview.id);
    if (INTERVIEW_MOCK_MODE) {
      // The mock driver never talks to Gemini, so there is no ephemeral token
      // to mint (and no GEMINI_API_KEY is required). Still carry the settings.
      setSession({
        token: 'mock',
        model: 'mock',
        voice: 'mock',
        systemInstruction:
          'Mock interviewer: this is a simulated practice interview. Answer as you would out loud, and we will wrap up when you click End interview.',
        maxDurationSeconds: interview.maxDurationSeconds ?? 600,
        maxQuestions: interview.maxQuestions ?? 10,
        maxFollowups: interview.maxFollowups ?? 2,
        expiresAt: '',
      });
    } else {
      const sres = await aiInterviewsService.session(interview.id);
      setSession(sres.data);
    }
  }, []);

  const createInterview = useCallback(
    async (payload: AiInterviewPayload) => {
      setLoadError(null);
      try {
        const res = await aiInterviewsService.create(applicationId, payload);
        setInterviews((prev) => [res.data, ...prev]);
        await prepareForInterview(res.data);
      } catch (err) {
        setLoadError(getApiErrorMessage(err, 'Could not create the interview.'));
        throw err;
      }
    },
    [applicationId, prepareForInterview]
  );

  const begin = useCallback(async () => {
    const current = selectedRef.current;
    if (!current) return;
    setLoadError(null);
    try {
      await prepareForInterview(current);
    } catch (err) {
      setLoadError(getApiErrorMessage(err, 'Could not start the interview.'));
    }
  }, [prepareForInterview]);

  const selectedRef = useRef<AiInterview | null>(null);
  selectedRef.current = selected;

  useEffect(() => {
    if (!session || startedRef.current) return;
    startedRef.current = true;
    setStage('CONNECTING');
    void driverRef.current.start();
  }, [session]);

  const submitAnswer = useCallback(
    (text: string) => {
      if (!INTERVIEW_MOCK_MODE) return;
      (driver as MockLiveSessionDriver).submitAnswer(text);
    },
    [driver]
  );

  const deleteInterview = useCallback(
    async (id: number) => {
      await aiInterviewsService.remove(id);
      setInterviews((prev) => prev.filter((i) => i.id !== id));
      if (interviewIdRef.current === id) {
        interviewIdRef.current = null;
        startedRef.current = false;
        finishingRef.current = false;
        setSession(null);
        setStage('IDLE');
      }
    },
    []
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await aiInterviewsService.listForApplication(applicationId);
      setInterviews(res.data);
    } catch (err) {
      setLoadError(getApiErrorMessage(err, 'Could not load interviews for this application.'));
    } finally {
      setLoading(false);
    }
  }, [applicationId]);

  useEffect(() => {
    void load();
  }, [load]);

  // A COMPLETED interview gets its evaluation asynchronously (the backend
  // finishes the request immediately and scores in the background). Keep an eye
  // on the result endpoint and swap the result in as soon as it lands.
  const pollForResult = useCallback(async (id: number) => {
    try {
      const res = await aiInterviewsService.getResult(id);
      return res.data;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    const interview = completed;
    if (!interview || interview.result || interview.id == null) return;
    let active = true;
    let attempts = 0;
    const timer = window.setInterval(async () => {
      attempts += 1;
      const result = await pollForResult(interview.id);
      if (!active) return;
      if (result) {
        window.clearInterval(timer);
        setInterviews((prev) =>
          prev.map((i) =>
            i.id === interview.id
              ? {
                  ...i,
                  result,
                  overallScore: result.overallScore,
                  technicalScore: result.technicalScore,
                  communicationScore: result.communicationScore,
                  problemSolvingScore: result.problemSolvingScore,
                  recommendation: result.recommendation,
                }
              : i
          )
        );
      } else if (attempts >= 20) {
        window.clearInterval(timer);
      }
    }, 2500);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [completed, pollForResult]);

  useEffect(
    () => () => {
      finishingRef.current = false;
      if (wrapTimerRef.current != null) window.clearInterval(wrapTimerRef.current);
      if (failsafeTimerRef.current != null) window.clearTimeout(failsafeTimerRef.current);
    },
    []
  );

  return {
    loading,
    loadError: loadError ?? topError,
    interviews,
    selected,
    completed,
    session,
    stage,
    driverState,
    aiSpeaking,
    micOn: driver.micOn,
    micError: driver.micError,
    transcript,
    answeredCount,
    maxQuestions: maxQuestionsRef.current,
    isMock: INTERVIEW_MOCK_MODE,
    createInterview,
    begin,
    submitAnswer,
    finishInterview,
    deleteInterview,
    reload: load,
  };
}